'use client';

import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import styles from 'styles/sunfire.forms.module.css';
import ProviderModal from './ProviderModal';
import { ProviderLocation } from 'types/SunFire';
import { getProviderLocations, searchProviders } from 'actions/Sunfire';
import { addProvider as addDbProvider, saveLocation } from 'actions/Database';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { addProvider } from 'slices/doctorSlice';
import CaretIcon from 'components/icons/CaretIcon';
import { isZipCode } from 'utils/formHelpers';
import {
  selectCurrentLocation,
  selectZipCode,
  setLocation,
} from 'slices/locationSlice';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';
import { ANALYTICS_EVENTS, capture } from 'lib/analytics';

interface ProviderSearchProps {
  title: string;
  subtitle: string;
}

const ProviderSearch = ({ title, subtitle }: ProviderSearchProps) => {
  const dispatch = useAppDispatch();
  const userZipCode = useAppSelector(selectZipCode);
  const currentLocation = useAppSelector(selectCurrentLocation);

  const [zipCode, setZipCode] = useState<string>(userZipCode);
  const [locations, setLocations] = useState<ProviderLocation[]>([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);

  const zipLabel = 'ZIP Code';
  const providerLabel = "Provider's last name";

  const modalRef = useRef<HTMLDialogElement | null>(null);

  const searchFn = useCallback(
    (name: string) => {
      capture(ANALYTICS_EVENTS.providerSearchPerformed);
      return searchProviders(name, zipCode ?? '', 50);
    },
    [zipCode]
  );

  const {
    query: name,
    results,
    isSearching,
    handleChange: handleNameChange,
    clearResults,
  } = useDebouncedSearch<ProviderLocation>(searchFn);

  // Backdrop click closes modal via DOM listener (jsx-a11y forbids onClick on dialog)
  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;

    const handleClick = (e: MouseEvent) => {
      if (e.target === dialog) {
        dialog.close();
      }
    };

    dialog.addEventListener('click', handleClick);

    return () => dialog.removeEventListener('click', handleClick);
  }, []);

  const getProviderDetails = useCallback(async (provider: ProviderLocation) => {
    setIsLoadingDetails(true);

    const providerLocations = await getProviderLocations(provider.id);
    setLocations(providerLocations);

    if (providerLocations.length > 0 && modalRef.current) {
      modalRef.current.showModal();
    }

    setIsLoadingDetails(false);
  }, []);

  const handleLocationChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const newZip = e.target.value;
      setZipCode(newZip);
      if (isZipCode(newZip)) {
        const updatedLocation = { ...currentLocation, zipcode: newZip };
        dispatch(setLocation(updatedLocation));
        try {
          await saveLocation(updatedLocation);
        } catch {
          // ignore — zip updated locally even if DB save fails
        }
      }
    },
    [dispatch, currentLocation]
  );

  const addProviderLocation = async (location: ProviderLocation) => {
    const added = await addDbProvider(location);
    dispatch(addProvider({ ...added, created_at: null }));
    capture(ANALYTICS_EVENTS.providerSaved);
    clearResults();
  };

  const closeModal = useCallback(() => {
    if (modalRef.current) {
      modalRef.current.close();
    }
  }, []);

  return (
    <>
      <form className="has-divider">
        <fieldset>
          <div className="form-header">
            <legend>{title}</legend>
            <p className="explanation-text">{subtitle}</p>
          </div>

          <div className="row">
            <div className="one-third-column">
              <label htmlFor="provider-zip" className="field-label">
                {zipLabel}
              </label>
              <input
                id="provider-zip"
                type="search"
                name="location"
                value={zipCode}
                autoComplete="off"
                onChange={handleLocationChange}
                placeholder="Enter 5-digit ZIP code"
              />
            </div>
            <div className="two-third-column">
              <label htmlFor="provider-name" className="field-label">
                {providerLabel} &#40;exclude titles like &ldquo;Dr&rdquo;&#41;
              </label>
              <input
                id="provider-name"
                type="search"
                name="name"
                placeholder="Start typing a provider's name to search..."
                value={name}
                autoComplete="off"
                onChange={handleNameChange}
                disabled={!isZipCode(zipCode)}
              />
              {isSearching && <p className="loading-spinner"></p>}
              {!isSearching && results.length > 0 && (
                <menu
                  className={styles.autocomplete}
                  aria-label="Provider search results"
                >
                  {results.map((provider) => {
                    return (
                      <li key={provider.id}>
                        <button
                          type="button"
                          className={styles.autocompleteBtn}
                          onClick={() => getProviderDetails(provider)}
                          disabled={isLoadingDetails}
                        >
                          <div>
                            <p>
                              <b>{provider.name}</b>
                            </p>
                            <p>{provider.specialties}</p>
                          </div>
                          <CaretIcon direction="right" size="large" />
                        </button>
                      </li>
                    );
                  })}
                </menu>
              )}
            </div>
          </div>
        </fieldset>
      </form>

      <ProviderModal
        modalRef={modalRef}
        locations={locations}
        addProviderLocation={addProviderLocation}
        onModalClose={closeModal}
      />
    </>
  );
};

export default ProviderSearch;
